import * as React from 'react';

interface IProps {
    image: string;
    id: string;
}

interface IState {}

class CompareImagesCanvas extends React.Component<IProps, IState> {
    canvas: any;
    canvasParent: HTMLElement;
    canvasWidth: number;
    canvasHeight: number;
    lastX: number;
    lastY: number;
    ctx: any;
    canvasImage: any;
    imageWidth: number;
    imageHeight: number;
    scaleFactor: number;
    initialScaleFactor: number;
    totalZooms: number;
    dragStart: any;
    dragged: any;
    ticker: any;
    rotation: number;

    constructor(props: IProps) {
        super(props);
        this.initialScaleFactor = 1;
        this.scaleFactor = 1.25;
        this.totalZooms = 1;
        this.rotation = 0;

        // Bind
        this.trackTransforms = this.trackTransforms.bind(this);
        this.redraw = this.redraw.bind(this);
        this.mousedown = this.mousedown.bind(this);
        this.mouseup = this.mouseup.bind(this);
        this.mousemove = this.mousemove.bind(this);
    }

    // Canvas transformations
    trackTransforms(ctx) {
        const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        let xForm = svg.createSVGMatrix();
        ctx.getTransform = function () {
            return xForm;
        };

        const savedTransforms = [];
        const save = ctx.save;
        ctx.save = () => {
            savedTransforms.push(xForm.translate(0, 0));
            return save.call(ctx);
        };

        const restore = ctx.restore;
        ctx.restore = () => {
            xForm = savedTransforms.pop();
            return restore.call(ctx);
        };

        const scale = ctx.scale;
        ctx.scale = (sx, sy) => {
            xForm = xForm.scaleNonUniform(sx, sy);
            return scale.call(ctx, sx, sy);
        };

        const rotate = ctx.rotate;
        ctx.rotate = (amount) => {
            xForm = xForm.rotate(amount);
            return rotate.call(ctx, amount);
        };

        const translate = ctx.translate;
        ctx.translate = (dx, dy) => {
            xForm = xForm.translate(dx, dy);
            return translate.call(ctx, dx, dy);
        };

        const transform = ctx.transform;
        ctx.transform = (a, b, c, d, e, f) => {
            let m2 = svg.createSVGMatrix();
            m2.a = a;
            m2.b = b;
            m2.c = c;
            m2.d = d;
            m2.e = e;
            m2.f = f;
            xForm = xForm.multiply(m2);
            return transform.call(ctx, a, b, c, d, e, f);
        };

        const setTransform = ctx.setTransform;
        ctx.setTransform = (a, b, c, d, e, f) => {
            xForm.a = a;
            xForm.b = b;
            xForm.c = c;
            xForm.d = d;
            xForm.e = e;
            xForm.f = f;
            return setTransform.call(ctx, a, b, c, d, e, f);
        };

        const pt = svg.createSVGPoint();
        ctx.transformedPoint = (x, y) => {
            pt.x = x;
            pt.y = y;
            return pt.matrixTransform(xForm.inverse());
        };
    }

    componentDidMount() {
        this.resizeCanvas();
        this.ctx = this.canvas.getContext('2d');
        this.trackTransforms(this.ctx);
        this.canvasImage = new Image();
        this.canvasImage.src = this.props.image;
        this.ctx.translate(this.canvasWidth / 2, this.canvasHeight / 2);
        this.canvasImage.onload = () => {
            this.imageWidth = this.canvasImage.width;
            this.imageHeight = this.canvasImage.height;
            this.setScale();
            this.ctx.scale(this.initialScaleFactor, this.initialScaleFactor);
            this.redraw();
        };
        window.addEventListener('resize', () => {
            clearTimeout(this.ticker);
            this.ticker = setTimeout(() => {
                this.resizeCanvas();
                this.resetImage();
            }, 200);
        });
    }

    componentDidUpdate(prevProps) {
        if (!this.canvasImage.src.includes(this.props.image)) {
            this.rotation = 0;
            this.resizeCanvas();
            this.ctx = this.canvas.getContext('2d');
            this.trackTransforms(this.ctx);
            this.canvasImage = new Image();
            this.canvasImage.src = this.props.image;
            this.ctx.translate(this.canvasWidth / 2, this.canvasHeight / 2);
            this.canvasImage.onload = () => {
                this.imageWidth = this.canvasImage.width;
                this.imageHeight = this.canvasImage.height;
                this.setScale();
                this.ctx.scale(this.initialScaleFactor, this.initialScaleFactor);
                this.redraw();
            };
            window.addEventListener('resize', () => {
                clearTimeout(this.ticker);
                this.ticker = setTimeout(() => {
                    this.resizeCanvas();
                    this.resetImage();
                }, 200);
            });
        }
    }

    resizeCanvas() {
        if (!this.canvas) {
            this.canvas = document.getElementById(this.props.id);
            this.canvasParent = document.getElementById(this.props.id + '_parent');
        }
        this.canvasWidth = this.canvasParent.offsetWidth;
        this.canvasHeight = this.canvasParent.offsetHeight;
        this.canvas.width = this.canvasWidth;
        this.canvas.height = this.canvasHeight;
        if (!this.lastX || !this.lastY) {
            this.lastX = this.canvasWidth / 2;
            this.lastY = this.canvasHeight / 2;
        }
        this.setScale();
    }

    setScale() {
        let xSize = this.canvasWidth - this.imageWidth;
        let ySize = this.canvasHeight - this.imageHeight;
        if (xSize < ySize || xSize === ySize) {
            this.initialScaleFactor =
                Math.round((this.canvasWidth / this.imageWidth) * 1000) / 1000;
        } else {
            this.initialScaleFactor =
                Math.round((this.canvasHeight / this.imageHeight) * 1000) / 1000;
        }
    }

    public zoom(clicks: number, redraw: boolean = true) {
        let factor = Math.pow(this.scaleFactor, clicks);
        this.ctx.scale(factor, factor);
        this.totalZooms =
            (this.totalZooms === 1 && clicks === -1) || (this.totalZooms === -1 && clicks === 1)
                ? this.totalZooms + clicks * 2
                : this.totalZooms + clicks;
        if (redraw) this.redraw();
    }

    public rotate(dir: string = 'cw') {
        let rad = dir === 'cw' ? Math.PI / 2 : -(Math.PI / 2);
        this.rotation = this.rotation === rad * 3 ? 0 : this.rotation + rad;
        this.redraw();
    }

    public resetImage() {
        let p1 = this.ctx.transformedPoint(0, 0);
        let p2 = this.ctx.transformedPoint(this.canvasWidth, this.canvasHeight);
        this.ctx.clearRect(p1.x, p1.y, p2.x - p1.x, p2.y - p1.y);
        this.ctx.setTransform(1, 0, 0, 1, 0, 0);
        this.ctx.translate(this.canvasWidth / 2, this.canvasHeight / 2);
        this.ctx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);
        this.ctx.scale(this.initialScaleFactor, this.initialScaleFactor);
        this.totalZooms = 1;
        this.rotation = 0;
        this.redraw();
    }

    redraw() {
        let p1 = this.ctx.transformedPoint(0, 0);
        let p2 = this.ctx.transformedPoint(this.canvasWidth, this.canvasHeight);
        this.ctx.clearRect(p1.x, p1.y, p2.x - p1.x, p2.y - p1.y);
        this.ctx.save();
        // horizontal scale: 1, vertical skew: 0, horizontal skew: 0, vertical scale: 1, x translate: 0, y translate: 0
        this.ctx.setTransform(1, 0, 0, 1, 0, 0);
        this.ctx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);
        this.ctx.restore();
        if (this.rotation !== 0) {
            this.ctx.save();
            this.ctx.rotate(this.rotation);
            this.ctx.drawImage(this.canvasImage, -this.imageWidth / 2, -this.imageHeight / 2);
            this.ctx.restore();
        } else {
            this.ctx.drawImage(this.canvasImage, -this.imageWidth / 2, -this.imageHeight / 2);
        }
    }

    mousedown(event: any) {
        document.body.style.userSelect = 'none';
        this.lastX = event.offsetX || event.pageX - this.canvas.offsetLeft;
        this.lastY = event.offsetY || event.pageY - this.canvas.offsetTop;
        this.dragStart = this.ctx.transformedPoint(this.lastX, this.lastY);
        this.dragged = false;
    }

    mousemove(event: any) {
        this.lastX = event.offsetX || event.pageX - this.canvas.offsetLeft;
        this.lastY = event.offsetY || event.pageY - this.canvas.offsetTop;
        this.dragged = true;
        if (this.dragStart) {
            let pt = this.ctx.transformedPoint(this.lastX, this.lastY);
            this.ctx.translate(pt.x - this.dragStart.x, pt.y - this.dragStart.y);
            this.redraw();
        }
    }

    mouseup() {
        this.dragStart = false;
    }

    render() {
        return (
            <div
                id={this.props.id + '_parent'}
                className="imageCanvasParent"
                style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    width: '100%',
                    height: '100%'
                }}
            >
                <canvas
                    id={this.props.id}
                    onMouseDown={this.mousedown}
                    onMouseUp={this.mouseup}
                    onMouseMove={this.mousemove}
                    onMouseOut={this.mouseup}
                    style={{
                        width: this.canvasWidth,
                        height: this.canvasHeight
                    }}
                ></canvas>
            </div>
        );
    }
}

export default CompareImagesCanvas;
